import { useState, useEffect } from "react";
import axios from "axios";
import QMSLayout from "../components/QMSLayout";
import OrgSelector from "../components/OrgSelector";
import { API } from "../App";
import { useAuth } from "../App";
import { Plus, AlertCircle, Search } from "lucide-react";
import { useTranslation } from "react-i18next";

const DeviationsPage = () => {
  const { t } = useTranslation(['capa', 'common']);
  const { isAdmin, isAuditor } = useAuth();
  const [deviations, setDeviations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [sectorFilter, setSectorFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    sector: "life-sciences",
    impact: "minor"
  });

  const fetchDeviations = async () => {
    try {
      let url = `${API}/deviations`;
      const params = [];
      if (filter !== "all") params.push(`status=${filter}`);
      if (sectorFilter !== "all") params.push(`sector=${sectorFilter}`);
      if (params.length) url += `?${params.join("&")}`;
      
      const response = await axios.get(url, { withCredentials: true });
      setDeviations(response.data);
    } catch (error) {
      console.error("Error fetching deviations:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeviations();
  }, [filter, sectorFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/deviations`, formData, { withCredentials: true });
      setShowModal(false);
      setFormData({ title: "", description: "", sector: "life-sciences", org_id: "", org_name: "", vendor_id: "", impact: "minor" });
      fetchDeviations();
    } catch (error) {
      console.error("Error creating deviation:", error);
    }
  };

  const handleStatusUpdate = async (devId, newStatus) => {
    try {
      await axios.put(`${API}/deviations/${devId}`, { status: newStatus }, { withCredentials: true });
      fetchDeviations();
    } catch (error) {
      console.error("Error updating deviation:", error);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      open: "badge-red",
      investigating: "badge-amber",
      resolved: "badge-blue",
      closed: "badge-green"
    };
    return badges[status] || "badge-gray";
  };

  const getImpactBadge = (impact) => {
    const badges = { minor: "badge-gray", major: "badge-amber", critical: "badge-red" };
    return badges[impact] || "badge-gray";
  };

  // KPIs
  const open = deviations.filter(d => d.status === "open").length;
  const investigating = deviations.filter(d => d.status === "investigating").length;
  const critical = deviations.filter(d => d.impact === "critical").length;

  return (
    <QMSLayout title={t('capa:deviations.title')}>
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="deviation-kpis">
        <div className="kpi-card" style={{ "--kpi-accent": "#36A9E1" }}>
          <p className="kpi-label">{t('capa:deviations.totalDeviations')}</p>
          <p className="kpi-value">{deviations.length}</p>
        </div>
        <div className="kpi-card" style={{ "--kpi-accent": "#F87171" }}>
          <p className="kpi-label">{t('capa:deviations.open')}</p>
          <p className="kpi-value">{open}</p>
        </div>
        <div className="kpi-card" style={{ "--kpi-accent": "#FBBF24" }}>
          <p className="kpi-label">{t('capa:deviations.investigating')}</p>
          <p className="kpi-value">{investigating}</p>
        </div>
        <div className="kpi-card" style={{ "--kpi-accent": "#F87171" }}>
          <p className="kpi-label">{t('capa:deviations.criticalImpact')}</p>
          <p className="kpi-value">{critical}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="filter-pills">
          {[
            { key: "all", label: t('capa:deviations.all') },
            { key: "open", label: t('capa:deviations.open') },
            { key: "investigating", label: t('capa:deviations.investigating') },
            { key: "resolved", label: t('capa:deviations.resolved') },
            { key: "closed", label: t('capa:deviations.closed') }
          ].map((item) => (
            <button
              key={item.key}
              onClick={() => setFilter(item.key)}
              className={`filter-pill ${filter === item.key ? "active" : ""}`}
            >
              {item.label}
            </button>
          ))}
        </div>

        <select
          value={sectorFilter}
          onChange={(e) => setSectorFilter(e.target.value)}
          className="qms-select w-auto"
        >
          <option value="all">{t('capa:deviations.allSectors')}</option>
          <option value="life-sciences">{t('capa:deviations.lifeSciences')}</option>
          <option value="aerospace">{t('capa:deviations.aerospace')}</option>
          <option value="industrial">{t('capa:deviations.industrial')}</option>
          <option value="iot">{t('capa:deviations.iot')}</option>
        </select>

        {(isAdmin || isAuditor) && (
          <button onClick={() => setShowModal(true)} className="btn-primary ml-auto" data-testid="create-deviation-btn">
            <Plus size={18} /> {t('capa:deviations.logDeviation')}
          </button>
        )}
      </div>

      {/* Table */}
      <div className="qms-card overflow-hidden" data-testid="deviations-table">
        <div className="overflow-x-auto">
          <table className="qms-table">
            <thead>
              <tr>
                <th>{t('capa:deviations.thId')}</th>
                <th>{t('capa:deviations.thTitle')}</th>
                <th>{t('capa:deviations.thSector')}</th>
                <th>{t('capa:deviations.thImpact')}</th>
                <th>{t('capa:deviations.thStatus')}</th>
                <th>{t('capa:deviations.thCreated')}</th>
                {(isAdmin || isAuditor) && <th>{t('capa:deviations.thActions')}</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-8 text-[#8B95A5]">{t('common:status.loading')}</td></tr>
              ) : deviations.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-8 text-[#8B95A5]">{t('capa:deviations.noDeviations')}</td></tr>
              ) : (
                deviations.map((dev) => (
                  <tr key={dev.deviation_id}>
                    <td className="font-mono text-[#38BDF8]">{dev.deviation_id}</td>
                    <td>
                      <div>
                        <p className="text-white">{dev.title}</p>
                        <p className="text-[#8B95A5] text-xs truncate max-w-xs">{dev.description}</p>
                      </div>
                    </td>
                    <td className="capitalize">{dev.sector.replace("-", " ")}</td>
                    <td>
                      <span className={`badge ${getImpactBadge(dev.impact)} capitalize`}>
                        {dev.impact}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${getStatusBadge(dev.status)} capitalize`}>
                        {dev.status}
                      </span>
                    </td>
                    <td className="text-[#8B95A5] text-sm">
                      {new Date(dev.created_at).toLocaleDateString()}
                    </td>
                    {(isAdmin || isAuditor) && (
                      <td>
                        <div className="flex gap-2">
                          {dev.status === "open" && (
                            <button
                              onClick={() => handleStatusUpdate(dev.deviation_id, "investigating")}
                              className="text-xs text-[#FBBF24] hover:underline"
                            >
                              {t('capa:deviations.investigate')}
                            </button>
                          )}
                          {dev.status === "investigating" && (
                            <button
                              onClick={() => handleStatusUpdate(dev.deviation_id, "resolved")}
                              className="text-xs text-[#36A9E1] hover:underline"
                            >
                              {t('capa:deviations.resolve')}
                            </button>
                          )}
                          {dev.status === "resolved" && (
                            <button
                              onClick={() => handleStatusUpdate(dev.deviation_id, "closed")}
                              className="text-xs text-[#4ADE80] hover:underline"
                            >
                              {t('capa:deviations.close')}
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b border-[rgba(255,255,255,0.07)]">
              <h3 className="font-['Syne'] text-lg font-semibold text-white">{t('capa:deviations.modalTitle')}</h3>
              <button onClick={() => setShowModal(false)} className="text-[#8B95A5] hover:text-white">×</button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-[#8B95A5] text-sm mb-1">{t('capa:deviations.labelTitle')}</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="qms-input"
                  required
                />
              </div>
              <div>
                <label className="block text-[#8B95A5] text-sm mb-1">{t('capa:deviations.labelDescription')}</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="qms-input min-h-[100px]"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#8B95A5] text-sm mb-1">{t('capa:deviations.labelSector')}</label>
                  <select
                    value={formData.sector}
                    onChange={(e) => setFormData({ ...formData, sector: e.target.value })}
                    className="qms-select"
                  >
                    <option value="life-sciences">{t('capa:deviations.lifeSciences')}</option>
                    <option value="aerospace">{t('capa:deviations.aerospace')}</option>
                    <option value="industrial">{t('capa:deviations.industrial')}</option>
                    <option value="iot">{t('capa:deviations.iot')}</option>
                  </select>
                </div>
                <OrgSelector
                  value={formData.org_id}
                  onChange={(orgId, orgName) => setFormData({...formData, org_id: orgId, org_name: orgName})}
                  showVendorId={true}
                  vendorId={formData.vendor_id}
                  onVendorIdChange={v => setFormData({...formData, vendor_id: v})}
                />
                <div>
                  <label className="block text-[#8B95A5] text-sm mb-1">{t('capa:deviations.labelImpact')}</label>
                  <select
                    value={formData.impact}
                    onChange={(e) => setFormData({ ...formData, impact: e.target.value })}
                    className="qms-select"
                  >
                    <option value="minor">{t('capa:deviations.minor')}</option>
                    <option value="major">{t('capa:deviations.major')}</option>
                    <option value="critical">{t('capa:deviations.critical')}</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="submit" className="btn-primary flex-1">Log Deviation</button>
                <button type="button" onClick={() => setShowModal(false)} className="btn-ghost">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </QMSLayout>
  );
};

export default DeviationsPage;
